import { Col, Input, Row, Spin, Table, Card, Statistic, Tag, Button, Space } from "antd";
import { useEffect, useState } from "react";
import {
    ShoppingOutlined,
    DollarOutlined,
    FileTextOutlined,
    CarOutlined,
    FunnelPlotOutlined,
    LoadingOutlined,
    ArrowLeftOutlined,
    DownloadOutlined,
    BankOutlined,
    PercentageOutlined,
    WalletOutlined,
    CreditCardOutlined,
    CheckCircleOutlined,
    CheckOutlined,
    CloseOutlined,
    WarningOutlined
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
    changeDateFrom,
    changeDateTo,
    changeBranch,
} from "../../redux/actions/authAction";
import { INPUT_SIZE } from "../../common/ThemeConstants";
import FormButton from "../../components/general/FormButton";
import FormSelect from "../../components/general/FormSelect";
import { postRequest } from "../../services/mainApp.service";
import { getDate } from "date-fns";
import { COMPANY_ADMIN } from "../../common/SetupMasterEnum.js";
import StatisticCard from "../../components/NewComponents/StatisticCard.jsx";
import LineChartComponent from "./LineChartComponent.jsx";
import { colorMap } from "./utils.js";

const ChartDetails = () => {
    const { RefreshStats } = useSelector((state) => state.RefreshReducer);
    const dispatch = useDispatch();
    const { type } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [orderData, setOrderData] = useState([]);
    const [summaryStats, setSummaryStats] = useState({});
    const [filteredData, setFilteredData] = useState([]);

    const appStore = useSelector((state) => state.authReducer);

    const {
        RoleId,
        dateFrom,
        dateTo,
        branchId,
    } = useSelector((state) => state.authReducer);

    const [data, setData] = useState({
        DateFrom: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        DateTo: new Date().toISOString().split('T')[0],
        BranchId: null,
    });

    // Add these processing functions for the new chart types
    const processTopProductsData = (orderData) => {
        if (!orderData || !orderData.length) return [];

        const productMap = {};

        orderData.forEach(order => {
            order.OrderDetails?.forEach(detail => {
                const productKey = `${detail.ProductName}-${detail.SizeName}`;

                if (!productMap[productKey]) {
                    productMap[productKey] = {
                        Product: detail.ProductName,
                        CategoryName: detail.CategoryName,
                        SizeName: detail.SizeName,
                        Quantity: 0,
                        SalesAmount: 0,
                        TimesSold: 0
                    };
                }

                productMap[productKey].Quantity += detail.Quantity || 0;
                productMap[productKey].SalesAmount += detail.PriceWithGST || 0;
                productMap[productKey].TimesSold += 1;
            });
        });

        // Calculate average price and format data
        const result = Object.values(productMap).map(product => ({
            ...product,
            AveragePrice: product.SalesAmount / product.Quantity,
            Product: product.SizeName !== '-' ?
                `${product.Product} (${product.SizeName})` : product.Product
        }));

        return result.sort((a, b) => b.Quantity - a.Quantity);
    };

    const processCategorySalesData = (orderData) => {
        if (!orderData || !orderData.length) return [];

        const categoryMap = {};
        let totalSales = 0;

        orderData.forEach(order => {
            order.OrderDetails?.forEach(detail => {
                const categoryName = detail.CategoryName;

                if (!categoryMap[categoryName]) {
                    categoryMap[categoryName] = {
                        CategoryName: categoryName,
                        TotalQuantity: 0,
                        SalesAmount: 0,
                        ProductCount: new Set(),
                        OrdersCount: 0
                    };
                }

                categoryMap[categoryName].TotalQuantity += detail.Quantity || 0;
                categoryMap[categoryName].SalesAmount += detail.PriceWithGST || 0;
                categoryMap[categoryName].ProductCount.add(detail.ProductName);
            });
        });

        // Calculate total sales for percentages
        totalSales = Object.values(categoryMap).reduce((sum, cat) => sum + cat.SalesAmount, 0);

        // Format the data
        const result = Object.values(categoryMap).map(category => ({
            CategoryName: category.CategoryName,
            TotalQuantity: category.TotalQuantity,
            SalesAmount: category.SalesAmount,
            ProductCount: category.ProductCount.size,
            Percentage: totalSales ? (category.SalesAmount / totalSales) * 100 : 0
        }));

        return result.sort((a, b) => b.SalesAmount - a.SalesAmount);
    };

    const processTopCustomersData = (orderData) => {
        if (!orderData || !orderData.length) return [];

        const customerMap = {};

        orderData.forEach(order => {
            const customerKey = order.OrderNumber; // This would typically be customer ID

            if (!customerMap[customerKey]) {
                customerMap[customerKey] = {
                    CustomerName: 'Customer', // Would be actual customer name
                    PhoneNumber: 'N/A', // Would be actual phone
                    Email: 'N/A', // Would be actual email
                    OrderCount: 0,
                    NetSales: 0,
                    Orders: []
                };
            }

            customerMap[customerKey].OrderCount += 1;
            customerMap[customerKey].NetSales += order.NetAmount || 0;
            customerMap[customerKey].Orders.push(order);
        });

        const result = Object.values(customerMap).map(customer => ({
            ...customer,
            AverageOrderValue: customer.NetSales / customer.OrderCount,
            CustomerName: `Customer ${Math.floor(Math.random() * 1000)}` // Placeholder - replace with actual data
        }));

        return result.sort((a, b) => b.NetSales - a.NetSales);
    };

    const chartConfigMap = {
        "top-products": {
            title: "Most Sold Products",
            columns: [
                {
                    title: 'Product Name',
                    dataIndex: 'Product',
                    key: 'Product',
                    render: (text) => <strong>{text}</strong>,
                    width: 200,
                    sorter: (a, b) => a.Product.localeCompare(b.Product),
                },
                {
                    title: 'Category',
                    dataIndex: 'CategoryName',
                    key: 'CategoryName',
                    width: 150,
                    filters: [
                        // These will be populated dynamically based on data
                        { text: 'Beef Burgers', value: 'Beef Burgers' },
                        { text: 'Chicken Burgers', value: 'Chicken Burgers' },
                        // Add more categories dynamically
                    ],
                    onFilter: (value, record) => record.CategoryName === value,
                    render: (category) => <Tag color="blue">{category}</Tag>,
                },
                {
                    title: 'Times Sold',
                    dataIndex: 'Quantity',
                    key: 'Quantity',
                    align: 'right',
                    width: 120,
                    sorter: (a, b) => a.Quantity - b.Quantity,
                    render: (quantity) => <span className="font-semibold">{quantity?.toLocaleString('en-PK')}</span>,
                },
                {
                    title: 'Sales Amount',
                    dataIndex: 'SalesAmount',
                    key: 'SalesAmount',
                    align: 'right',
                    width: 140,
                    sorter: (a, b) => a.SalesAmount - b.SalesAmount,
                    render: (amount) => `Rs. ${amount?.toLocaleString('en-PK') || '0'}`,
                },
                {
                    title: 'Average Price',
                    dataIndex: 'AveragePrice',
                    key: 'AveragePrice',
                    align: 'right',
                    width: 140,
                    sorter: (a, b) => a.AveragePrice - b.AveragePrice,
                    render: (amount) => `Rs. ${amount?.toFixed(2) || '0'}`,
                }
            ]
        },

        "category-sales": {
            title: "Sales by Category",
            columns: [
                {
                    title: 'Category Name',
                    dataIndex: 'CategoryName',
                    key: 'CategoryName',
                    render: (text) => <strong>{text}</strong>,
                    width: 200,
                    sorter: (a, b) => a.CategoryName.localeCompare(b.CategoryName),
                },
                {
                    title: 'Number of Products',
                    dataIndex: 'ProductCount',
                    key: 'ProductCount',
                    align: 'center',
                    width: 140,
                    sorter: (a, b) => a.ProductCount - b.ProductCount,
                    render: (count) => <span className="font-semibold">{count}</span>,
                },
                {
                    title: 'Total Quantity Sold',
                    dataIndex: 'TotalQuantity',
                    key: 'TotalQuantity',
                    align: 'right',
                    width: 150,
                    sorter: (a, b) => a.TotalQuantity - b.TotalQuantity,
                    render: (quantity) => <span>{quantity?.toLocaleString('en-PK')}</span>,
                },
                {
                    title: 'Sales Amount',
                    dataIndex: 'SalesAmount',
                    key: 'SalesAmount',
                    align: 'right',
                    width: 140,
                    sorter: (a, b) => a.SalesAmount - b.SalesAmount,
                    render: (amount) => `Rs. ${amount?.toLocaleString('en-PK') || '0'}`,
                },
                {
                    title: 'Percentage',
                    dataIndex: 'Percentage',
                    key: 'Percentage',
                    align: 'right',
                    width: 120,
                    sorter: (a, b) => a.Percentage - b.Percentage,
                    render: (percentage) => (
                        <Tag color={percentage > 20 ? 'green' : percentage > 10 ? 'blue' : 'orange'}>
                            {percentage?.toFixed(2)}%
                        </Tag>
                    ),
                }
            ]
        },

        "top-customers": {
            title: "Top Customers",
            columns: [
                {
                    title: 'Customer Name',
                    dataIndex: 'CustomerName',
                    key: 'CustomerName',
                    render: (text) => <strong>{text || 'Guest Customer'}</strong>,
                    width: 180,
                    sorter: (a, b) => (a.CustomerName || '').localeCompare(b.CustomerName || ''),
                },
                {
                    title: 'Phone Number',
                    dataIndex: 'PhoneNumber',
                    key: 'PhoneNumber',
                    width: 150,
                    render: (phone) => phone ? (
                        <span className="text-blue-600">{phone}</span>
                    ) : (
                        <span className="text-gray-400">Not Provided</span>
                    ),
                },
                {
                    title: 'Email',
                    dataIndex: 'Email',
                    key: 'Email',
                    width: 200,
                    render: (email) => email ? (
                        <span className="text-blue-600">{email}</span>
                    ) : (
                        <span className="text-gray-400">Not Provided</span>
                    ),
                },
                {
                    title: 'Total Orders',
                    dataIndex: 'OrderCount',
                    key: 'OrderCount',
                    align: 'right',
                    width: 120,
                    sorter: (a, b) => a.OrderCount - b.OrderCount,
                    render: (count) => <span className="font-semibold">{count}</span>,
                },
                {
                    title: 'Total Spending',
                    dataIndex: 'NetSales',
                    key: 'NetSales',
                    align: 'right',
                    width: 150,
                    sorter: (a, b) => a.NetSales - b.NetSales,
                    render: (amount) => `Rs. ${amount?.toLocaleString('en-PK') || '0'}`,
                },
                {
                    title: 'Average Order Value',
                    dataIndex: 'AverageOrderValue',
                    key: 'AverageOrderValue',
                    align: 'right',
                    width: 160,
                    sorter: (a, b) => a.AverageOrderValue - b.AverageOrderValue,
                    render: (amount) => `Rs. ${amount?.toLocaleString('en-PK') || '0'}`,
                },
                {
                    title: 'Customer Type',
                    dataIndex: 'CustomerType',
                    key: 'CustomerType',
                    width: 130,
                    render: (type, record) => {
                        const orderCount = record.OrderCount || 0;
                        const totalSpending = record.NetSales || 0;

                        let customerType = 'New';
                        let color = 'blue';

                        if (orderCount > 5 && totalSpending > 10000) {
                            customerType = 'VIP';
                            color = 'gold';
                        } else if (orderCount > 2) {
                            customerType = 'Regular';
                            color = 'green';
                        } else if (totalSpending > 5000) {
                            customerType = 'High Value';
                            color = 'purple';
                        }

                        return <Tag color={color}>{customerType}</Tag>;
                    },
                }
            ]
        },
        "order-by-branch": {
            title: "Order By Branch",
            filterKey: "BranchName",
            columns: [
                {
                    title: 'Order Number',
                    dataIndex: 'OrderNumber',
                    key: 'OrderNumber',
                    render: (text) => <strong>{text}</strong>,
                    width: 180,
                },
                {
                    title: 'Branch',
                    dataIndex: 'BranchName',
                    key: 'BranchName',
                    width: 120,
                    filters: [
                        { text: 'DHA', value: 'DHA' },
                        { text: 'Gulshan', value: 'Gulshan' },
                        // Add other branches dynamically
                    ],
                    onFilter: (value, record) => record.BranchName === value,
                    render: (branch) => <Tag color="purple">{branch}</Tag>,
                },
                {
                    title: 'Order Date',
                    dataIndex: 'OrderDateTime',
                    key: 'OrderDateTime',
                    width: 150,
                    render: (date) => date ? new Date(date).toLocaleString() : '-',
                },
                {
                    title: 'Status',
                    dataIndex: 'OrderStatus',
                    key: 'OrderStatus',
                    width: 100,
                    render: (status) => {
                        const statusColors = {
                            'Confirmed': 'blue',
                            'Cancel': 'red',
                            'Paid': 'green',
                            'Pending': 'orange'
                        };
                        return <Tag color={statusColors[status] || 'default'}>{status}</Tag>;
                    },
                },
                {
                    title: 'Net Amount',
                    dataIndex: 'NetAmount',
                    key: 'NetAmount',
                    align: 'right',
                    width: 120,
                    render: (amount) => `Rs. ${amount?.toLocaleString('en-PK') || '0'}`,
                }
            ]
        },
        "shift-wise-orders": {
            title: "Shift-Wise Orders",
            filterKey: "ShiftName",
            columns: [
                {
                    title: 'Order Number',
                    dataIndex: 'OrderNumber',
                    key: 'OrderNumber',
                    render: (text) => <strong>{text}</strong>,
                    width: 180,
                },
                {
                    title: 'Shift',
                    dataIndex: 'ShiftName',
                    key: 'ShiftName',
                    width: 120,
                    render: (shift) => {
                        const shiftColors = {
                            'Morning Shift': 'green',
                            'Evening Shift': 'orange',
                            'Night Shift': 'blue',
                            'No Shift': 'gray'
                        };
                        return <Tag color={shiftColors[shift] || 'default'}>{shift || 'Not Assigned'}</Tag>;
                    },
                },
                {
                    title: 'Order Date',
                    dataIndex: 'OrderDateTime',
                    key: 'OrderDateTime',
                    width: 150,
                    render: (date) => date ? new Date(date).toLocaleString() : '-',
                },
                {
                    title: 'Business Date',
                    dataIndex: 'BusinessDate',
                    key: 'BusinessDate',
                    width: 150,
                    render: (date) => date ? new Date(date).toLocaleDateString() : '-',
                },
                {
                    title: 'Status',
                    dataIndex: 'OrderStatus',
                    key: 'OrderStatus',
                    width: 100,
                    render: (status) => {
                        const statusColors = {
                            'Confirmed': 'blue',
                            'Cancel': 'red',
                            'Paid': 'green',
                            'Pending': 'orange'
                        };
                        return <Tag color={statusColors[status] || 'default'}>{status}</Tag>;
                    },
                },
                {
                    title: 'Net Amount',
                    dataIndex: 'NetAmount',
                    key: 'NetAmount',
                    align: 'right',
                    width: 120,
                    render: (amount) => `Rs. ${amount?.toLocaleString('en-PK') || '0'}`,
                }
            ]
        },
        "sale-details": {
            title: "Sale Details",
            columns: [
                {
                    title: 'Order Number',
                    dataIndex: 'OrderNumber',
                    key: 'OrderNumber',
                    render: (text) => <strong>{text}</strong>,
                    width: 180,
                },
                {
                    title: 'Order Date',
                    dataIndex: 'OrderDateTime',
                    key: 'OrderDateTime',
                    width: 150,
                    sorter: (a, b) => new Date(a.OrderDateTime) - new Date(b.OrderDateTime),
                    render: (date) => date ? new Date(date).toLocaleDateString() : '-',
                },
                {
                    title: 'Gross Sale',
                    dataIndex: 'GrossAmount',
                    key: 'GrossAmount',
                    align: 'right',
                    width: 120,
                    sorter: (a, b) => a.GrossAmount - b.GrossAmount,
                    render: (amount) => `Rs. ${amount?.toLocaleString('en-PK') || '0'}`,
                },
                {
                    title: 'Net Sale',
                    dataIndex: 'NetAmount',
                    key: 'NetAmount',
                    align: 'right',
                    width: 120,
                    sorter: (a, b) => a.NetAmount - b.NetAmount,
                    render: (amount) => `Rs. ${amount?.toLocaleString('en-PK') || '0'}`,
                },
                {
                    title: 'Delivery Charges',
                    dataIndex: 'DeliveryCharges',
                    key: 'DeliveryCharges',
                    align: 'right',
                    width: 140,
                    sorter: (a, b) => a.DeliveryCharges - b.DeliveryCharges,
                    render: (charges) => `Rs. ${charges?.toLocaleString('en-PK') || '0'}`,
                },
                {
                    title: 'Discount',
                    dataIndex: 'Discount',
                    key: 'Discount',
                    align: 'right',
                    width: 120,
                    sorter: (a, b) => a.Discount - b.Discount,
                    render: (discount) => `Rs. ${discount?.toLocaleString('en-PK') || '0'}`,
                },
                {
                    title: 'Tax Amount',
                    dataIndex: 'Gst',
                    key: 'Gst',
                    align: 'right',
                    width: 120,
                    sorter: (a, b) => a.Gst - b.Gst,
                    render: (tax) => `Rs. ${tax?.toLocaleString('en-PK') || '0'}`,
                },
                {
                    title: 'Status',
                    dataIndex: 'OrderStatus',
                    key: 'OrderStatus',
                    width: 100,
                    render: (status) => {
                        const statusColors = {
                            'Confirmed': 'blue',
                            'Cancel': 'red',
                            'Paid': 'green',
                            'Pending': 'orange'
                        };
                        return <Tag color={statusColors[status] || 'default'}>{status}</Tag>;
                    },
                },
                {
                    title: 'Branch',
                    dataIndex: 'BranchName',
                    key: 'BranchName',
                    width: 120,
                }
            ]
        },
        "order-by-status": {
            title: "Order By Status",
            filterKey: "OrderStatus",
            columns: [
                {
                    title: 'Order Number',
                    dataIndex: 'OrderNumber',
                    key: 'OrderNumber',
                    render: (text) => <strong>{text}</strong>,
                    width: 180,
                },
                {
                    title: 'Status',
                    dataIndex: 'OrderStatus',
                    key: 'OrderStatus',
                    width: 120,
                    filters: [
                        { text: 'Confirmed', value: 'Confirmed' },
                        { text: 'Cancel', value: 'Cancel' },
                        { text: 'Paid', value: 'Paid' },
                        { text: 'Pending', value: 'Pending' },
                    ],
                    onFilter: (value, record) => record.OrderStatus === value,
                    render: (status) => {
                        const statusColors = {
                            'Confirmed': 'blue',
                            'Cancel': 'red',
                            'Paid': 'green',
                            'Pending': 'orange'
                        };
                        return <Tag color={statusColors[status] || 'default'}>{status}</Tag>;
                    },
                },
                {
                    title: 'Order Date',
                    dataIndex: 'OrderDateTime',
                    key: 'OrderDateTime',
                    width: 150,
                    sorter: (a, b) => new Date(a.OrderDateTime) - new Date(b.OrderDateTime),
                    render: (date) => date ? new Date(date).toLocaleDateString() : '-',
                },
                {
                    title: 'Order Mode',
                    dataIndex: 'OrderMode',
                    key: 'OrderMode',
                    width: 120,
                    filters: [
                        { text: 'Delivery', value: 'Delivery' },
                        { text: 'DineIn', value: 'DineIn' },
                        { text: 'Pickup', value: 'Pickup' },
                    ],
                    onFilter: (value, record) => record.OrderMode === value,
                },
                {
                    title: 'Net Amount',
                    dataIndex: 'NetAmount',
                    key: 'NetAmount',
                    align: 'right',
                    width: 120,
                    sorter: (a, b) => a.NetAmount - b.NetAmount,
                    render: (amount) => `Rs. ${amount?.toLocaleString('en-PK') || '0'}`,
                },
                {
                    title: 'Branch',
                    dataIndex: 'BranchName',
                    key: 'BranchName',
                    width: 120,
                }
            ]
        },
        "order-by-source": {
            title: "Order By Source",
            filterKey: "OrderSource",
            columns: [
                {
                    title: 'Order Number',
                    dataIndex: 'OrderNumber',
                    key: 'OrderNumber',
                    render: (text) => <strong>{text}</strong>,
                    width: 180,
                },
                {
                    title: 'Source',
                    dataIndex: 'OrderSource',
                    key: 'OrderSource',
                    width: 100,
                    filters: [
                        { text: 'Web', value: 'Web' },
                        { text: 'POS', value: 'POS' },
                        { text: 'Call', value: 'Call' },
                    ],
                    onFilter: (value, record) => record.OrderSource === value,
                    render: (source) => {
                        const sourceColors = {
                            'Web': 'blue',
                            'POS': 'green',
                            'Call': 'orange'
                        };
                        return <Tag color={sourceColors[source] || 'default'}>{source}</Tag>;
                    },
                },
                {
                    title: 'Order Mode',
                    dataIndex: 'OrderMode',
                    key: 'OrderMode',
                    width: 120,
                },
                {
                    title: 'Status',
                    dataIndex: 'OrderStatus',
                    key: 'OrderStatus',
                    width: 100,
                    render: (status) => {
                        const statusColors = {
                            'Confirmed': 'blue',
                            'Cancel': 'red',
                            'Paid': 'green',
                            'Pending': 'orange'
                        };
                        return <Tag color={statusColors[status] || 'default'}>{status}</Tag>;
                    },
                },
                {
                    title: 'Order Date',
                    dataIndex: 'OrderDateTime',
                    key: 'OrderDateTime',
                    width: 150,
                    render: (date) => date ? new Date(date).toLocaleString() : '-',
                },
                {
                    title: 'Net Amount',
                    dataIndex: 'NetAmount',
                    key: 'NetAmount',
                    align: 'right',
                    width: 120,
                    render: (amount) => `Rs. ${amount?.toLocaleString('en-PK') || '0'}`,
                }
            ]
        },
        "payment-type": {
            title: "Payment Type",
            filterKey: "PaymentMode",
            columns: [
                {
                    title: 'Order Number',
                    dataIndex: 'OrderNumber',
                    key: 'OrderNumber',
                    render: (text) => <strong>{text}</strong>,
                    width: 180,
                },
                {
                    title: 'Payment Mode',
                    dataIndex: 'PaymentMode',
                    key: 'PaymentMode',
                    width: 130,
                    filters: [
                        { text: 'CASH', value: 'CASH' },
                        { text: 'CARD', value: 'CARD' },
                    ],
                    onFilter: (value, record) => record.PaymentMode === value,
                    render: (mode) => <Tag color={mode === 'CASH' ? 'green' : 'blue'}>{mode || 'Not Specified'}</Tag>,
                },
                {
                    title: 'Status',
                    dataIndex: 'OrderStatus',
                    key: 'OrderStatus',
                    width: 100,
                    render: (status) => {
                        const statusColors = {
                            'Confirmed': 'blue',
                            'Cancel': 'red',
                            'Paid': 'green',
                            'Pending': 'orange'
                        };
                        return <Tag color={statusColors[status] || 'default'}>{status}</Tag>;
                    },
                },
                {
                    title: 'Received Amount',
                    dataIndex: 'Received_Amount',
                    key: 'Received_Amount',
                    align: 'right',
                    width: 140,
                    render: (amount) => `Rs. ${amount?.toLocaleString('en-PK') || '0'}`,
                },
                {
                    title: 'Change Amount',
                    dataIndex: 'ChangeAmount',
                    key: 'ChangeAmount',
                    align: 'right',
                    width: 140,
                    render: (amount) => `Rs. ${amount?.toLocaleString('en-PK') || '0'}`,
                },
                {
                    title: 'Tip',
                    dataIndex: 'Tip',
                    key: 'Tip',
                    align: 'right',
                    width: 100,
                    render: (tip) => `Rs. ${tip?.toLocaleString('en-PK') || '0'}`,
                }
            ]
        },
        "sales-by-category": {
            title: "Sales by Category",
            columns: [
                {
                    title: 'Order Number',
                    dataIndex: 'OrderNumber',
                    key: 'OrderNumber',
                    render: (text) => <strong>{text}</strong>,
                    width: 180,
                },
                {
                    title: 'Products',
                    dataIndex: 'OrderDetails',
                    key: 'OrderDetails',
                    render: (details) => (
                        <div className="max-w-xs">
                            {details?.slice(0, 3).map((detail, index) => (
                                <div key={index} className="text-xs mb-1">
                                    <span className="font-medium">{detail.Quantity}x</span> {detail.ProductName}
                                    {detail.SizeName && detail.SizeName !== '-' && ` (${detail.SizeName})`}
                                </div>
                            ))}
                            {details?.length > 3 && (
                                <div className="text-xs text-blue-600">+{details.length - 3} more items</div>
                            )}
                        </div>
                    ),
                },
                {
                    title: 'Categories',
                    dataIndex: 'OrderDetails',
                    key: 'Categories',
                    render: (details) => {
                        const categories = [...new Set(details?.map(detail => detail.CategoryName))];
                        return (
                            <div className="max-w-xs">
                                {categories.slice(0, 2).map((cat, index) => (
                                    <Tag key={index} color="blue" className="mb-1">{cat}</Tag>
                                ))}
                                {categories.length > 2 && (
                                    <Tag color="default">+{categories.length - 2} more</Tag>
                                )}
                            </div>
                        );
                    },
                },
                {
                    title: 'Status',
                    dataIndex: 'OrderStatus',
                    key: 'OrderStatus',
                    width: 100,
                    render: (status) => {
                        const statusColors = {
                            'Confirmed': 'blue',
                            'Cancel': 'red',
                            'Paid': 'green',
                            'Pending': 'orange'
                        };
                        return <Tag color={statusColors[status] || 'default'}>{status}</Tag>;
                    },
                },
                {
                    title: 'Net Amount',
                    dataIndex: 'NetAmount',
                    key: 'NetAmount',
                    align: 'right',
                    width: 120,
                    render: (amount) => `Rs. ${amount?.toLocaleString('en-PK') || '0'}`,
                }
            ]
        },
        "sales-by-area": {
            title: "Sales by Area",
            filterKey: "BranchName",
            columns: [
                {
                    title: 'Order Number',
                    dataIndex: 'OrderNumber',
                    key: 'OrderNumber',
                    render: (text) => <strong>{text}</strong>,
                    width: 180,
                },
                {
                    title: 'Branch/Area',
                    dataIndex: 'BranchName',
                    key: 'BranchName',
                    width: 120,
                    filters: [
                        { text: 'DHA', value: 'DHA' },
                    ],
                    onFilter: (value, record) => record.BranchName === value,
                    render: (branch) => <Tag color="purple">{branch}</Tag>,
                },
                {
                    title: 'Order Date',
                    dataIndex: 'OrderDateTime',
                    key: 'OrderDateTime',
                    width: 150,
                    render: (date) => date ? new Date(date).toLocaleString() : '-',
                },
                {
                    title: 'Status',
                    dataIndex: 'OrderStatus',
                    key: 'OrderStatus',
                    width: 100,
                    render: (status) => {
                        const statusColors = {
                            'Confirmed': 'blue',
                            'Cancel': 'red',
                            'Paid': 'green',
                            'Pending': 'orange'
                        };
                        return <Tag color={statusColors[status] || 'default'}>{status}</Tag>;
                    },
                },
                {
                    title: 'Delivery Charges',
                    dataIndex: 'DeliveryCharges',
                    key: 'DeliveryCharges',
                    align: 'right',
                    width: 140,
                    render: (charges) => `Rs. ${charges?.toLocaleString('en-PK') || '0'}`,
                },
                {
                    title: 'Net Amount',
                    dataIndex: 'NetAmount',
                    key: 'NetAmount',
                    align: 'right',
                    width: 120,
                    render: (amount) => `Rs. ${amount?.toLocaleString('en-PK') || '0'}`,
                }
            ]
        },
        "customers-type": {
            title: "Customers Analysis",
            columns: [
                {
                    title: 'Order Number',
                    dataIndex: 'OrderNumber',
                    key: 'OrderNumber',
                    render: (text) => <strong>{text}</strong>,
                    width: 180,
                },
                {
                    title: 'Customer Type',
                    dataIndex: 'OrderDetails',
                    key: 'CustomerType',
                    width: 130,
                    render: (details, record) => {
                        // Simple logic to determine customer type based on order pattern
                        const isHighValue = record.NetAmount > 2000;
                        const isFrequent = orderData.filter(order =>
                            order.OrderNumber !== record.OrderNumber &&
                            order.NetAmount > 500
                        ).length > 2;

                        if (isHighValue && isFrequent) {
                            return <Tag color="gold">VIP Customer</Tag>;
                        } else if (isHighValue) {
                            return <Tag color="green">High Value</Tag>;
                        } else {
                            return <Tag color="blue">Regular</Tag>;
                        }
                    },
                },
                {
                    title: 'Order Date',
                    dataIndex: 'OrderDateTime',
                    key: 'OrderDateTime',
                    width: 150,
                    render: (date) => date ? new Date(date).toLocaleString() : '-',
                },
                {
                    title: 'Items Count',
                    dataIndex: 'OrderDetails',
                    key: 'ItemsCount',
                    width: 100,
                    align: 'center',
                    render: (details) => details?.length || 0,
                },
                {
                    title: 'Order Value',
                    dataIndex: 'NetAmount',
                    key: 'NetAmount',
                    align: 'right',
                    width: 120,
                    sorter: (a, b) => a.NetAmount - b.NetAmount,
                    render: (amount) => `Rs. ${amount?.toLocaleString('en-PK') || '0'}`,
                }
            ]
        },
        "order-by-gender": {
            title: "Customers by Gender",
            columns: [
                {
                    title: 'Order Number',
                    dataIndex: 'OrderNumber',
                    key: 'OrderNumber',
                    render: (text) => <strong>{text}</strong>,
                    width: 180,
                },
                {
                    title: 'Order Date',
                    dataIndex: 'OrderDateTime',
                    key: 'OrderDateTime',
                    width: 150,
                    render: (date) => date ? new Date(date).toLocaleString() : '-',
                },
                {
                    title: 'Customer Type',
                    dataIndex: 'OrderSource',
                    key: 'CustomerType',
                    width: 120,
                    render: (source) => {
                        const typeColors = {
                            'Web': 'cyan',
                            'POS': 'purple',
                            'Call': 'orange'
                        };
                        return <Tag color={typeColors[source] || 'default'}>{source || 'Unknown'}</Tag>;
                    },
                },
                {
                    title: 'Status',
                    dataIndex: 'OrderStatus',
                    key: 'OrderStatus',
                    width: 100,
                    render: (status) => {
                        const statusColors = {
                            'Confirmed': 'blue',
                            'Cancel': 'red',
                            'Paid': 'green',
                            'Pending': 'orange'
                        };
                        return <Tag color={statusColors[status] || 'default'}>{status}</Tag>;
                    },
                },
                {
                    title: 'Net Amount',
                    dataIndex: 'NetAmount',
                    key: 'NetAmount',
                    align: 'right',
                    width: 120,
                    render: (amount) => `Rs. ${amount?.toLocaleString('en-PK') || '0'}`,
                }
            ]
        }
    };

    useEffect(() => {
        if (RefreshStats == 1) {
            getOrderData();
            dispatch({ type: "REFRESH_STATS", payload: 0 });
        }
    }, [RefreshStats]);

    const getOrderData = () => {
        setLoading(true);

        const dateFromValue = RoleId === 14 ? dateFrom : data.DateFrom;
        const dateToValue = RoleId === 14 ? dateTo : data.DateTo;

        postRequest("Dashboard_Order", {
            OperationId: 1,
            CompanyId: appStore.CompanyId,
            BranchId:
                appStore.userBranchList?.length > 0 &&
                    appStore.IsPos === true &&
                    appStore.RoleId !== COMPANY_ADMIN
                    ? appStore.userBranchList[0]?.BranchId
                    : RoleId === 14
                        ? branchId
                        : data.BranchId,
            DateFrom: dateFromValue || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            DateTo: dateToValue || new Date().toISOString().split('T')[0]
        }).then((res) => {
            setLoading(false);
            console.log("API Response:", res);
            const orders = res?.data?.Data || [];
            console.log("Processed orders:", orders);
            setOrderData(orders);
            calculateSummaryStats(orders);
            applyFilters(orders);
        }).catch((err) => {
            setLoading(false);
            console.log("API Error:", err);
        });
    };

    const calculateSummaryStats = (orders) => {
        const stats = {
            totalOrders: orders.length,
            totalRevenue: orders.reduce((sum, order) => sum + (order.NetAmount || 0), 0),
            totalTax: orders.reduce((sum, order) => sum + (order.Gst || 0), 0),
            totalDeliveryCharges: orders.reduce((sum, order) => sum + (order.DeliveryCharges || 0), 0),
            totalDiscount: orders.reduce((sum, order) => sum + (order.Discount || 0), 0),
            confirmedOrders: orders.filter(order => order.OrderStatus === 'Confirmed').length,
            cancelledOrders: orders.filter(order => order.OrderStatus === 'Cancel').length,
            paidOrders: orders.filter(order => order.OrderStatus === 'Paid').length,
            pendingOrders: orders.filter(order => order.OrderStatus === 'Pending').length,
        };
        setSummaryStats(stats);
        console.log("Calculated stats:", stats);
    };

    const applyFilters = (orders) => {
        if (!orders.length) {
            setFilteredData([]);
            return;
        }

        const config = chartConfigMap[type];
        if (!config) {
            setFilteredData(orders);
            return;
        }

        let filtered = [...orders];

        // Handle different data processing based on chart type
        switch (type) {
            case "top-products":
                filtered = processTopProductsData(orders);
                break;

            case "category-sales":
                filtered = processCategorySalesData(orders);
                break;

            case "top-customers":
                filtered = processTopCustomersData(orders);
                break;

            case "order-by-status":
                filtered = orders;
                break;

            case "payment-type":
                filtered = orders.filter(order => order.PaymentMode === null ? order : order);
                break;

            default:
                filtered = orders;
                break;
        }

        setFilteredData(filtered);
        console.log("Filtered data for", type, ":", filtered);
    };
    const getCurrentConfig = () => {
        return chartConfigMap[type] || {
            title: "Order Details",
            columns: [
                {
                    title: 'Order Number',
                    dataIndex: 'OrderNumber',
                    key: 'OrderNumber',
                    width: 180,
                },
                {
                    title: 'Status',
                    dataIndex: 'OrderStatus',
                    key: 'OrderStatus',
                    width: 120,
                },
                {
                    title: 'Net Amount',
                    dataIndex: 'NetAmount',
                    key: 'NetAmount',
                    width: 120,
                }
            ]
        };
    };

    const exportToCSV = () => {
        const headers = ['Order Number', 'Status', 'Order Date', 'Net Amount', 'Branch', 'Order Source', 'Payment Mode'];
        const csvData = filteredData.map(order => [
            order.OrderNumber,
            order.OrderStatus,
            new Date(order.OrderDateTime).toLocaleDateString(),
            order.NetAmount,
            order.BranchName,
            order.OrderSource,
            order.PaymentMode || 'N/A'
        ]);

        const csvContent = [
            headers.join(','),
            ...csvData.map(row => row.join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${type}-data-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    useEffect(() => {
        console.log("Chart type changed to:", type);
        getOrderData();
    }, [type]);

    useEffect(() => {
        if (orderData.length > 0) {
            console.log("Order data updated, applying filters");
            applyFilters(orderData);
        }
    }, [type, orderData]);

    const processChartData = () => {
        if (!orderData.length) {
            console.log("No order data available for chart");
            return [];
        }

        const config = chartConfigMap[type];
        if (!config) {
            console.log("No config found for type:", type);
            return [];
        }

        let chartData = [];

        switch (type) {
            case "sale-details":
                const saleDetailsData = orderData.reduce((acc, order) => {
                    const date = order.OrderDateTime ? order.OrderDateTime.split('T')[0] : 'Unknown';

                    if (!acc[date]) {
                        acc[date] = {
                            date,
                            grossSale: 0,
                            netSale: 0,
                            deliveryCharges: 0,
                            discount: 0,
                            tax: 0
                        };
                    }

                    acc[date].grossSale += order.GrossAmount || 0;
                    acc[date].netSale += order.NetAmount || 0;
                    acc[date].deliveryCharges += order.DeliveryCharges || 0;
                    acc[date].discount += order.Discount || 0;
                    acc[date].tax += order.Gst || 0;

                    return acc;
                }, {});

                chartData = Object.values(saleDetailsData)
                    .sort((a, b) => new Date(a.date) - new Date(b.date))
                    .slice(-15);
                break;
            case "order-by-status":
                const statusData = orderData.reduce((acc, order) => {
                    const date = order.OrderDateTime ? order.OrderDateTime.split('T')[0] : 'Unknown';
                    const status = order.OrderStatus || 'Unknown';

                    if (!acc[date]) {
                        acc[date] = { date };
                    }

                    if (!acc[date][status]) {
                        acc[date][status] = 0;
                    }
                    acc[date][status]++;

                    return acc;
                }, {});

                chartData = Object.values(statusData)
                    .sort((a, b) => new Date(a.date) - new Date(b.date))
                    .slice(-15);
                break;

            case "order-by-source":
                const sourceData = orderData.reduce((acc, order) => {
                    const date = order.OrderDateTime ? order.OrderDateTime.split('T')[0] : 'Unknown';
                    const source = order.OrderSource || 'Unknown';

                    if (!acc[date]) {
                        acc[date] = { date };
                    }

                    if (!acc[date][source]) {
                        acc[date][source] = 0;
                    }
                    acc[date][source]++;

                    return acc;
                }, {});

                chartData = Object.values(sourceData)
                    .sort((a, b) => new Date(a.date) - new Date(b.date))
                    .slice(-15);
                break;

            case "payment-type":
                const paymentData = orderData.reduce((acc, order) => {
                    const date = order.OrderDateTime ? order.OrderDateTime.split('T')[0] : 'Unknown';
                    const paymentMode = order.PaymentMode || 'Not Specified';

                    if (!acc[date]) {
                        acc[date] = { date };
                    }

                    if (!acc[date][paymentMode]) {
                        acc[date][paymentMode] = 0;
                    }
                    acc[date][paymentMode]++;

                    return acc;
                }, {});

                chartData = Object.values(paymentData)
                    .sort((a, b) => new Date(a.date) - new Date(b.date))
                    .slice(-15);
                break;

            case "sales-by-area":
                const areaData = orderData.reduce((acc, order) => {
                    const date = order.OrderDateTime ? order.OrderDateTime.split('T')[0] : 'Unknown';
                    const branch = order.BranchName || 'Unknown';

                    if (!acc[date]) {
                        acc[date] = { date };
                    }

                    if (!acc[date][branch]) {
                        acc[date][branch] = 0;
                    }
                    acc[date][branch]++;

                    return acc;
                }, {});

                chartData = Object.values(areaData)
                    .sort((a, b) => new Date(a.date) - new Date(b.date))
                    .slice(-15);
                break;

            case "sales-by-category":
                const salesData = orderData.reduce((acc, order) => {
                    const date = order.OrderDateTime ? order.OrderDateTime.split('T')[0] : 'Unknown';

                    if (!acc[date]) {
                        acc[date] = { date, sales: 0 };
                    }
                    acc[date].sales += order.NetAmount || 0;

                    return acc;
                }, {});

                chartData = Object.values(salesData)
                    .sort((a, b) => new Date(a.date) - new Date(b.date))
                    .slice(-15);
                break;

            case "order-by-gender":
                const customerData = orderData.reduce((acc, order) => {
                    const date = order.OrderDateTime ? order.OrderDateTime.split('T')[0] : 'Unknown';
                    if (!acc[date]) {
                        acc[date] = { date, count: 0 };
                    }
                    acc[date].count++;
                    return acc;
                }, {});

                chartData = Object.values(customerData)
                    .sort((a, b) => new Date(a.date) - new Date(b.date))
                    .slice(-15);
                break;

            case "top-products":
                const productTrendData = orderData.reduce((acc, order) => {
                    const date = order.OrderDateTime ? order.OrderDateTime.split('T')[0] : 'Unknown';

                    if (!acc[date]) {
                        acc[date] = { date, totalProducts: 0, totalSales: 0 };
                    }

                    order.OrderDetails?.forEach(detail => {
                        acc[date].totalProducts += detail.Quantity || 0;
                        acc[date].totalSales += detail.PriceWithGST || 0;
                    });

                    return acc;
                }, {});

                chartData = Object.values(productTrendData)
                    .sort((a, b) => new Date(a.date) - new Date(b.date))
                    .slice(-15);
                break;

            case "category-sales":
                // Group by date and category sales
                const categoryTrendData = orderData.reduce((acc, order) => {
                    const date = order.OrderDateTime ? order.OrderDateTime.split('T')[0] : 'Unknown';

                    if (!acc[date]) {
                        acc[date] = { date, categorySales: 0 };
                    }

                    order.OrderDetails?.forEach(detail => {
                        acc[date].categorySales += detail.PriceWithGST || 0;
                    });

                    return acc;
                }, {});

                chartData = Object.values(categoryTrendData)
                    .sort((a, b) => new Date(a.date) - new Date(b.date))
                    .slice(-15);
                break;

            case "top-customers":
                // Group by date and customer activity
                const customerTrendData = orderData.reduce((acc, order) => {
                    const date = order.OrderDateTime ? order.OrderDateTime.split('T')[0] : 'Unknown';

                    if (!acc[date]) {
                        acc[date] = { date, customerCount: 0, orderCount: 0 };
                    }

                    acc[date].customerCount += 1; // This would be unique customers in real scenario
                    acc[date].orderCount += 1;

                    return acc;
                }, {});

                chartData = Object.values(customerTrendData)
                    .sort((a, b) => new Date(a.date) - new Date(b.date))
                    .slice(-15);
                break;
            default:
                const ordersByDate = orderData.reduce((acc, order) => {
                    const date = order.OrderDateTime ? order.OrderDateTime.split('T')[0] : 'Unknown';
                    if (!acc[date]) {
                        acc[date] = { date, count: 0 };
                    }
                    acc[date].count++;
                    return acc;
                }, {});

                chartData = Object.values(ordersByDate)
                    .sort((a, b) => new Date(a.date) - new Date(b.date))
                    .slice(-15);
                break;
        }

        console.log("Processed chart data for", type, ":", chartData);
        return chartData;
    };

    const getChartKeys = () => {
        const data = processChartData();
        if (!data.length) {
            console.log("No chart data available");
            return [];
        }

        const keys = new Set();
        data.forEach(item => {
            Object.keys(item).forEach(key => {
                if (key !== 'date' && key !== 'sales' && key !== 'count') {
                    keys.add(key);
                }
            });
        });

        if (type === "sale-details" && data.some(item => item.grossSale !== undefined)) {
            return ['grossSale', 'netSale', 'deliveryCharges', 'discount', 'tax'];
        }

        if (type === "sales-by-category" && data.some(item => item.sales !== undefined)) {
            return ['sales'];
        }
        // Add these conditions
        if (type === "top-products" && data.some(item => item.totalProducts !== undefined)) {
            return ['totalProducts', 'totalSales'];
        }

        if (type === "category-sales" && data.some(item => item.categorySales !== undefined)) {
            return ['categorySales'];
        }

        if (type === "top-customers" && data.some(item => item.customerCount !== undefined)) {
            return ['customerCount', 'orderCount'];
        }
        if ((type === "customers-type" || type === "order-by-gender") && data.some(item => item.count !== undefined)) {
            return ['count'];
        }

        if (keys.size === 0 && data.some(item => item.count !== undefined)) {
            return ['count'];
        }

        const result = Array.from(keys);
        console.log("Chart keys for", type, ":", result);
        return result;
    };

    const chartData = processChartData();
    const chartKeys = getChartKeys();


    const getLineColor = (key, index) => {
        return colorMap[key] || colorMap.default[index % colorMap.default.length];
    };

    useEffect(() => {
        console.log("Current state:", {
            type,
            orderDataLength: orderData.length,
            filteredDataLength: filteredData.length,
            chartData,
            chartKeys,
            summaryStats
        });
    }, [type, orderData, filteredData, chartData, chartKeys, summaryStats]);

    const currentConfig = getCurrentConfig();

    return (
        <div style={{ display: "flex", height: "100%", width: "100%", minHeight: '500px' }}>
            <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
                {/* Header Section */}
                <Card className="mb-6 bg-transparent border-0 ">
                    <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                        <div className="flex items-center">
                            <Button
                                type="text"
                                icon={<ArrowLeftOutlined />}
                                onClick={() => navigate(-1)}
                                className="flex items-center justify-center h-10 w-10 hover:bg-white hover:shadow-sm mr-3 border border-gray-200"
                            >
                            </Button>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-800">{currentConfig.title} - Detailed View</h1>
                                <p className="text-gray-500 text-sm mt-1">
                                    Analyzing your business performance metrics
                                </p>
                            </div>
                        </div>

                        <Space size="middle" className="flex-wrap">
                            <div className="bg-white px-3 py-2 rounded-lg border border-gray-200">
                                <span className="text-sm font-medium text-gray-600">
                                    Showing: <span className="text-blue-600 font-semibold">{filteredData.length}</span> of{" "}
                                    <span className="text-gray-800 font-semibold">{orderData.length}</span> records
                                </span>
                            </div>
                            <Button
                                icon={<DownloadOutlined />}
                                onClick={exportToCSV}
                                disabled={filteredData.length === 0}
                                type="primary"
                                className="bg-blue-600 hover:bg-blue-700 border-0 shadow-sm"
                                size="middle"
                            >
                                Export CSV
                            </Button>
                        </Space>
                    </div>
                </Card>

                {/* Filters Section */}
                <Card className="mb-6 shadow-sm border-0">
                    <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-4 mb-4">
                        <div>
                            <h2 className="text-lg font-semibold text-gray-800">Filters & Controls</h2>
                            <p className="text-gray-500 text-sm">Refine your data view with date range and branch selection</p>
                        </div>
                        <div className="text-xs text-gray-400">
                            Last sync: {new Date().toLocaleTimeString()}
                        </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4">
                        <Row gutter={[16, 16]}>
                            <Col xs={24} sm={12} md={6}>
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Date From
                                    </label>
                                    <Input
                                        type="date"
                                        className="w-full h-10 border-gray-300 hover:border-blue-400 px-2 focus:border-blue-500"
                                        value={RoleId === 14 ? dateFrom : data.DateFrom}
                                        onChange={(e) => {
                                            RoleId === 14
                                                ? dispatch(changeDateFrom(e.target.value))
                                                : setData({ ...data, DateFrom: e.target.value });
                                        }}
                                    />
                                </div>
                            </Col>

                            <Col xs={24} sm={12} md={6}>
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Date To
                                    </label>
                                    <Input
                                        type="date"
                                        className="w-full px-2 h-10 border-gray-300 hover:border-blue-400 focus:border-blue-500"
                                        value={RoleId === 14 ? dateTo : data.DateTo}
                                        onChange={(e) => {
                                            RoleId === 14
                                                ? dispatch(changeDateTo(e.target.value))
                                                : setData({ ...data, DateTo: e.target.value });
                                        }}
                                    />
                                </div>
                            </Col>

                            <Col xs={24} sm={12} md={6}>
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Select Branch
                                    </label>
                                    <FormSelect
                                        listItem={
                                            RoleId === 14
                                                ? branchDropdownList.filter(
                                                    (company) => company.CompanyId === CompanyId
                                                )
                                                : appStore.userBranchList || []
                                        }
                                        idName="BranchId"
                                        valueName="BranchName"
                                        size={INPUT_SIZE}
                                        className="w-full px-2 h-10 border-gray-300 hover:border-blue-400 focus:border-blue-500"
                                        value={RoleId === 14 ? branchId : data.BranchId}
                                        onChange={(e) => {
                                            RoleId === 14
                                                ? dispatch(changeBranch(e.value))
                                                : setData({ ...data, BranchId: e.value });
                                        }}
                                    />
                                </div>
                            </Col>

                            <Col xs={24} sm={12} md={6}>
                                <div className="flex items-end h-full pt-2">
                                    <FormButton
                                        title="Apply Filters"
                                        icon={<FunnelPlotOutlined />}
                                        sm={6}
                                        type="primary"
                                        onClick={getOrderData}
                                        className="w-full h-10 bg-blue-600 hover:bg-blue-700 border-0 shadow-sm"
                                    />
                                </div>
                            </Col>
                        </Row>
                    </div>
                </Card>

                <div className="mb-6">
                    <LineChartComponent
                        data={chartData}
                        keys={chartKeys}
                        type={type}
                        title={`${currentConfig.title} - Daily Trend`}
                        loading={loading}
                        getLineColor={getLineColor}
                    />
                </div>

                {/* Data Table */}
                <Card
                    title={
                        <div className="flex justify-between items-center">
                            <span>{currentConfig.title} - Order Details</span>
                            <div className="text-sm text-gray-500">
                                Last updated: {new Date().toLocaleString()}
                            </div>
                        </div>
                    }
                    style={{ flex: 1 }}
                >
                    {loading ? (
                        <div className="flex justify-center items-center h-64">
                            <Spin />
                        </div>
                    ) : (
                        <div style={{ height: 'calc(100vh - 400px)', minHeight: '400px' }}>
                            <Table
                                dataSource={filteredData}
                                columns={currentConfig.columns}
                                rowKey="OrderMasterId"
                                pagination={{
                                    pageSize: 20,
                                    showSizeChanger: true,
                                    showQuickJumper: true,
                                    showTotal: (total, range) =>
                                        `${range[0]}-${range[1]} of ${total} items`
                                }}
                                scroll={{ x: 1000, y: 'calc(100vh - 500px)' }}
                                size="middle"
                                loading={loading}
                                onChange={(pagination, filters, sorter) => {
                                    console.log('Table changed:', { pagination, filters, sorter });
                                }}
                            />
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
};

export default ChartDetails;